// =====================================================================
// Lawyers Directory Rebuild V2 — shared design tokens (PKG-0).
//
// Values are EXACT from REBUILDV2/SPEC-TOKENS.md (handoff "TransferPitch
// Lawyers.dc.html") — brief RULE 1: the artboard wins, never round or
// normalize. None of these hexes exist in tailwind.config.js (theme
// `primary` is #3B82F6, `tp-primary` #111827 — different values), so
// they live here as plain strings for Tailwind arbitrary values, e.g.
//   className={`bg-[${LAW.primary}]`}  →  bg-[#2283FF]
// or inline `style` where a class can't express it. For shadows/scrims
// used inside a Tailwind arbitrary value, replace spaces with
// underscores (shadow-[0_1px_2px_rgba(16,24,40,0.05)]) — the *_TW
// constants below are pre-formatted for that.
//
// Only cross-artboard tokens live here; one-off per-surface values stay
// inline in the surface component per the artboard.
// =====================================================================

// ── Brand / primary blue ─────────────────────────────────────────────
export const LAW = {
  // primary actions: buttons, active filter borders, toggle ON, checkbox
  // checked, progress fill, text links
  primary: "#2283FF",
  // primary button hover
  primaryHover: "#1570E8",
  // active chip/badge text, verified badge text+check, shield icon
  primaryText: "#1567D2",
  // verified badge bg, active chip/pill bg, completion banner bg, tag bg
  primaryBg50: "#EBF3FF",
  // verified badge / chip / banner / tag border
  primaryBorder: "#C9E1FF",
  // completion progress track
  progressTrack: "#D3E5FD",
  // completion banner note text
  bannerNoteText: "#3D6FB4",
  // empty-state large search icon stroke
  emptyIcon: "#8FB6EC",

  // ── Neutrals — text ────────────────────────────────────────────────
  text900: "#101828", // headings, names, values, strong counts
  text700: "#344054", // field labels, secondary button text, back arrow
  text600: "#475467", // body text, inactive filter text, contact rows
  text500: "#667085", // subtitles, firm lines, helper text, close icons
  text400: "#98A2B3", // placeholders, muted labels, contact icons

  // ── Neutrals — surface ─────────────────────────────────────────────
  pageBg: "#F5F7FB", // canvas/page background
  surface: "#ffffff", // cards, panels, modals, sheets, inputs
  surfaceSubtle: "#F8FAFD", // license/trust box, expanded accordion header
  chipBg: "#F7F8FA", // specialization chip bg
  emptyIconBg: "#EEF3FB", // empty-state icon circle

  // ── Neutrals — borders ─────────────────────────────────────────────
  borderInput: "#DDE3EC", // inputs, selects, search boxes, secondary buttons
  borderCard: "#E6EAF2", // cards, accordions, sidebar divider
  borderChip: "#E7EBF0", // specialization chip border
  borderAvatar: "#E1E6EF", // photo avatar ring
  divider: "#EEF1F6", // card footer / panel header / mobile bar borders
  borderMuted: "#D5DBE7", // dashed empty border, toggle OFF, grabber
  borderDashedAdd: "#C9D2E0", // 1.5px dashed add-affordances (settings)

  // ── Dual-role (purple) — "Agent & Lawyer" pill ─────────────────────
  dualBg: "#F4F1FE",
  dualText: "#6941C6",
  dualBorder: "#E4DBFB",

  // ── Success (green) ────────────────────────────────────────────────
  successBg: "#E7F8EF", // success icon circle, live pill bg
  successIcon: "#12B76A", // success/verified check
  successBorder: "#B7E7CC", // live pill border
  successText: "#0E7A45", // "● Live in directory"

  // ── Warning (amber) — "● Incomplete" pill ──────────────────────────
  warnBg: "#FFF6E8",
  warnBorder: "#F5DDAE",
  warnText: "#B25E09",

  // ── Alert/missing (orange) — "· missing" label suffix ──────────────
  missingText: "#E04F16",
};

// ── Skeleton greys (loading board) ───────────────────────────────────
export const LAW_SKELETON = {
  strong: "#E4E9F1", // filter bars, strong blocks
  card: "#EBEFF5", // card blocks
  light: "#F1F4F8", // light blocks
  dimmed: "#EFF2F7", // dimmed-backdrop bars
};

// ── Overlays / scrims (CSS color strings) ────────────────────────────
export const LAW_SCRIM = {
  panel: "rgba(16,24,40,.28)", // detail side panel
  modal: "rgba(16,24,40,.32)", // centered modals
  sheet: "rgba(16,24,40,.35)", // mobile bottom sheets
};

// ── Shadows (CSS strings; *_TW = Tailwind-arbitrary-ready) ───────────
export const LAW_SHADOW = {
  card: "0 1px 2px rgba(16,24,40,.05)",
  panel: "-12px 0 40px rgba(16,24,40,.18)",
  modal: "0 20px 60px rgba(16,24,40,.25)",
  sheet: "0 -12px 40px rgba(16,24,40,.2)",
  toggleKnob: "0 1px 2px rgba(0,0,0,.2)",
};
export const LAW_SHADOW_TW = {
  card: "shadow-[0_1px_2px_rgba(16,24,40,0.05)]",
  panel: "shadow-[-12px_0_40px_rgba(16,24,40,0.18)]",
  modal: "shadow-[0_20px_60px_rgba(16,24,40,0.25)]",
  sheet: "shadow-[0_-12px_40px_rgba(16,24,40,0.2)]",
  toggleKnob: "shadow-[0_1px_2px_rgba(0,0,0,0.2)]",
};

// ── Radii scale (px numbers per SPEC-TOKENS) ─────────────────────────
export const LAW_RADIUS = {
  pill: 999, // pills, chips, badges, avatars, toggles, grabber
  modal: 16, // modals, empty-state box
  card: 14, // lawyer cards
  accordionMobile: 13, // mobile accordion sections
  settingsCard: 12, // settings cards, completion banner
  licenseBox: 11, // license/trust box, mobile search/buttons/sheet CTAs
  searchDesktop: 10, // desktop search + filter dropdowns, mobile card button
  control: 9, // desktop buttons, inputs/selects
  sidebarNav: 8, // settings sidebar nav items
  checkbox: 6, // checkboxes (19×19), skeleton text bars
};

// ── Motion — skeleton pulse (inject once per SPEC-TOKENS "Motion") ───
export const LAW_SKELETON_KEYFRAMES =
  "@keyframes lawSk{0%{opacity:.55}50%{opacity:1}100%{opacity:.55}}" +
  ".law-sk{animation:lawSk 1.4s infinite}";
